(() => {
  if (customElements.get('elonail-rewards')) return;

  const SOURCE = 'elonail-rewards';
  const GIFT_PROPERTY = '_elonail_gift';
  const RULE_PROPERTY = '_elonail_gift_rule';

  class ElonailRewards extends HTMLElement {
    connectedCallback() {
      if (window.__elonailRewardsOwner && window.__elonailRewardsOwner !== this) return;
      window.__elonailRewardsOwner = this;

      this.config = this.readConfig();
      this.running = false;
      this.pending = false;
      this.timer = null;
      this.currentCart = null;
      this.cartPanel = null;
      this.floating = this.querySelector('[data-reward-floating]');
      this.modal = this.querySelector('[data-reward-modal]');
      this.recommendationCache = new Map();
      this.lastQuickAddTrigger = null;

      window.ELONAIL_REWARDS_CONFIG = this.config;
      window.dispatchEvent(new CustomEvent('elonail:rewards:ready', { detail: { config: this.config } }));

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
          if (event.source === SOURCE) return;
          this.queueReconcile();
        });
      }

      this.onPageShow = () => this.queueReconcile(40);
      this.onDocumentClick = (event) => this.handleDocumentClick(event);
      this.onKeydown = (event) => this.handleKeydown(event);

      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      this.queueReconcile(80);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this.timer);
      document.body.classList.remove('elonail-reward-modal-open');
      if (window.__elonailRewardsOwner === this) delete window.__elonailRewardsOwner;
    }

    readConfig() {
      const node = this.querySelector('[data-elonail-rewards-config]');
      if (!node) return { enabled: false, tiers: [] };

      try {
        const parsed = JSON.parse(node.textContent || '{}');
        parsed.tiers = Array.isArray(parsed.tiers) ? parsed.tiers : [];
        parsed.quickAdd = parsed.quickAdd || {};
        parsed.quickAdd.collectionProducts = Array.isArray(parsed.quickAdd.collectionProducts)
          ? parsed.quickAdd.collectionProducts
          : [];
        parsed.shipping = parsed.shipping || {};
        parsed.reward = parsed.reward || {};
        return parsed;
      } catch (error) {
        console.error('[Rewards] Invalid configuration', error);
        return { enabled: false, tiers: [] };
      }
    }

    queueReconcile(delay = 140) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.reconcile(), delay);
    }

    async reconcile() {
      if (this.running) {
        this.pending = true;
        return;
      }

      this.running = true;

      try {
        const cart = await this.fetchCart();
        if (!cart?.items) return;

        this.currentCart = cart;
        const purchaseQuantity = this.getPurchaseQuantity(cart);
        const activeTier = this.getActiveTier(purchaseQuantity);
        const plan = this.buildGiftPlan(cart, activeTier);

        if (this.config.enabled && plan.hasChanges) {
          if (Object.keys(plan.updates).length) await this.updateGiftLines(plan.updates);
          if (plan.additions.length) await this.addGiftLines(plan.additions, activeTier);

          const finalCart = await this.fetchCart();
          this.currentCart = finalCart;
          await this.syncThemeCart(finalCart);
          this.emitStatus(finalCart);
        } else {
          this.emitStatus(cart);
        }
      } catch (error) {
        console.error('[Rewards] Unable to reconcile cart', error);
      } finally {
        this.running = false;
        if (this.pending) {
          this.pending = false;
          this.queueReconcile(80);
        }
      }
    }

    async fetchCart() {
      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    }

    getPurchaseQuantity(cart) {
      return cart.items.reduce((total, item) => {
        if (item.properties?.[GIFT_PROPERTY] === 'true' || item.properties?.[RULE_PROPERTY]) return total;
        return total + Number(item.quantity || 0);
      }, 0);
    }

    getPreparedTiers() {
      const tiers = (this.config.tiers || [])
        .filter((tier) => tier?.enabled)
        .map((tier, index) => ({
          ...tier,
          _index: index,
          threshold: Number(tier.threshold || 0),
          promotionText: String(tier.promotionText || '').trim(),
          gifts: Array.isArray(tier.gifts) ? tier.gifts : [],
        }))
        .filter((tier) => tier.threshold > 0);

      tiers.sort((a, b) => a.threshold - b.threshold || a._index - b._index);
      return tiers;
    }

    getActiveTier(purchaseQuantity) {
      if (!this.config.enabled) return null;
      return this.getPreparedTiers().filter((tier) => purchaseQuantity >= tier.threshold).pop() || null;
    }

    getNextTier(purchaseQuantity) {
      if (!this.config.enabled) return null;
      return this.getPreparedTiers().find((tier) => tier.threshold > purchaseQuantity) || null;
    }

    getTierRewardLabel(tier) {
      if (!tier) return '';
      if (tier.promotionText) return tier.promotionText;
      const count = tier.gifts?.length || 0;
      if (count === 1) return '+1 Free Gift';
      if (count > 1) return `+${count} Free Gifts`;
      return 'Reward';
    }

    getDesiredGiftQuantities(activeTier) {
      const desired = new Map();
      if (!activeTier) return desired;

      (activeTier.gifts || []).forEach((gift) => {
        if (!gift?.available || Number(gift.variantId) <= 0) return;
        const variantId = String(gift.variantId);
        desired.set(variantId, (desired.get(variantId) || 0) + 1);
      });

      return desired;
    }

    buildGiftPlan(cart, activeTier) {
      const desired = this.getDesiredGiftQuantities(activeTier);
      const remaining = new Map(desired);
      const updates = {};

      cart.items.forEach((item) => {
        const ruleId = item.properties?.[RULE_PROPERTY];
        const isManagedGift = item.properties?.[GIFT_PROPERTY] === 'true' || Boolean(ruleId);
        if (!isManagedGift) return;

        const variantId = String(item.variant_id || item.id || '');
        const belongsToActiveTier = activeTier && ruleId === activeTier.id;
        const desiredQuantity = belongsToActiveTier ? remaining.get(variantId) || 0 : 0;
        const keepQuantity = Math.min(Number(item.quantity || 0), desiredQuantity);

        if (Number(item.quantity || 0) !== keepQuantity) updates[item.key] = keepQuantity;
        if (belongsToActiveTier && desiredQuantity > 0) {
          remaining.set(variantId, Math.max(0, desiredQuantity - keepQuantity));
        }
      });

      const additions = [];
      remaining.forEach((quantity, variantId) => {
        if (quantity > 0) additions.push({ variantId: Number(variantId), quantity });
      });

      return {
        updates,
        additions,
        hasChanges: Object.keys(updates).length > 0 || additions.length > 0,
      };
    }

    async updateGiftLines(updates) {
      const url = window.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error(`Gift update failed: ${response.status}`);
      const data = await response.json();
      if (data?.errors) throw new Error(typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors));
      return data;
    }

    async addGiftLines(additions, activeTier) {
      if (!activeTier || !additions.length) return null;

      const url = window.routes?.cart_add_url || '/cart/add.js';
      const items = additions.map((item) => ({
        id: item.variantId,
        quantity: item.quantity,
        properties: {
          [GIFT_PROPERTY]: 'true',
          [RULE_PROPERTY]: activeTier.id,
        },
      }));

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error(`Gift add failed: ${response.status}`);
      const data = await response.json();
      if (data?.status) throw new Error(data.description || data.message || 'Unable to add gift');
      return data;
    }

    async syncThemeCart(cart) {
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        try {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: SOURCE,
            cartData: cart,
          });
        } catch (error) {
          console.warn('[Rewards] Cart subscribers did not fully refresh', error);
        }
      }

      await Promise.allSettled([this.refreshCartIcon(), this.refreshMainCartFooter()]);
      setTimeout(() => {
        this.ensureCartPanel();
        this.emitStatus(this.currentCart || cart);
      }, 120);
    }

    async refreshCartIcon() {
      const target = document.getElementById('cart-icon-bubble');
      if (!target) return;

      const response = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    }

    async refreshMainCartFooter() {
      const target = document.querySelector('#main-cart-footer .js-contents');
      if (!target) return;

      const sectionId = document.getElementById('main-cart-footer')?.dataset?.id;
      if (!sectionId) return;

      const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('#main-cart-footer .js-contents, .js-contents');
      if (source) target.innerHTML = source.innerHTML;
    }

    buildStatus(cart) {
      const purchaseQuantity = this.getPurchaseQuantity(cart);
      const tiers = this.getPreparedTiers();
      const activeTier = this.getActiveTier(purchaseQuantity);
      const nextTier = this.getNextTier(purchaseQuantity);
      const shippingThreshold = Math.max(0, Number(this.config.shipping?.threshold || 0));
      const shippingThresholdCents = Math.round(shippingThreshold * 100);
      const cartTotal = Number(cart.total_price || 0);
      const shippingRemaining = Math.max(0, shippingThresholdCents - cartTotal);

      return {
        purchaseQuantity,
        activeTier,
        nextTier,
        tiers,
        currency: cart.currency || window.Shopify?.currency?.active || 'USD',
        shipping: {
          enabled: Boolean(this.config.shipping?.enabled) && shippingThresholdCents > 0,
          thresholdCents: shippingThresholdCents,
          cartTotal,
          remainingCents: shippingRemaining,
          unlocked: shippingThresholdCents > 0 && cartTotal >= shippingThresholdCents,
          progress: shippingThresholdCents > 0 ? Math.max(0, Math.min(100, (cartTotal / shippingThresholdCents) * 100)) : 0,
        },
      };
    }

    emitStatus(cart) {
      if (!cart?.items) return;
      const detail = this.buildStatus(cart);
      this.ensureCartPanel();
      this.renderCartStatus(detail);
      this.renderFloatingStatus(detail);

      window.ELONAIL_REWARDS_STATUS = detail;
      window.dispatchEvent(new CustomEvent('elonail:rewards:status', { detail }));
      window.dispatchEvent(new CustomEvent('elonail:gift:status', { detail }));
    }

    ensureCartPanel() {
      const drawer = document.getElementById('CartDrawer');
      const header = drawer?.querySelector('.drawer__header');
      const template = this.querySelector('[data-reward-cart-template]');
      if (!drawer || !header || !template) {
        this.cartPanel = null;
        return null;
      }

      let panel = drawer.querySelector('[data-reward-cart-panel]');
      if (!panel) {
        const fragment = template.content.cloneNode(true);
        panel = fragment.querySelector('[data-reward-cart-panel]');
        header.insertAdjacentElement('afterend', panel);
      }

      this.cartPanel = panel;
      return panel;
    }

    renderCartStatus(detail) {
      const panel = this.cartPanel || this.ensureCartPanel();
      if (!panel) return;

      panel.hidden = !this.config.enabled;
      if (!this.config.enabled) return;

      const count = panel.querySelector('[data-reward-cart-count]');
      if (count) count.textContent = `${detail.purchaseQuantity} item${detail.purchaseQuantity === 1 ? '' : 's'}`;

      this.renderShippingRow({
        row: panel.querySelector('[data-shipping-row]'),
        label: panel.querySelector('[data-shipping-label]'),
        text: panel.querySelector('[data-shipping-text]'),
        progress: panel.querySelector('[data-shipping-progress]'),
        detail,
      });

      this.renderRewardRow({
        row: panel.querySelector('[data-reward-row]'),
        text: panel.querySelector('[data-reward-text]'),
        progress: panel.querySelector('[data-reward-progress]'),
        thumb: panel.querySelector('[data-reward-thumb]'),
        addButton: panel.querySelector('[data-reward-quick-add-open]'),
        detail,
      });
    }

    renderFloatingStatus(detail) {
      if (!this.floating) return;

      const hasContent = detail.shipping.enabled || detail.tiers.length > 0;
      this.floating.hidden = !this.config.enabled || !hasContent;
      if (this.floating.hidden) return;

      this.renderShippingRow({
        row: this.floating.querySelector('[data-floating-shipping-row]'),
        text: this.floating.querySelector('[data-floating-shipping-text]'),
        progress: this.floating.querySelector('[data-floating-shipping-progress]'),
        detail,
      });

      const title = this.floating.querySelector('[data-floating-reward-title]');
      const displayTier = detail.nextTier || detail.activeTier;
      if (title) title.textContent = displayTier ? this.getTierRewardLabel(displayTier) : 'Rewards';

      this.renderRewardRow({
        row: this.floating.querySelector('[data-floating-reward-row]'),
        text: this.floating.querySelector('[data-floating-reward-text]'),
        progress: this.floating.querySelector('[data-floating-reward-progress]'),
        thumb: this.floating.querySelector('[data-floating-reward-thumb]'),
        addButton: this.floating.querySelector('[data-reward-quick-add-open]'),
        detail,
      });
    }

    renderShippingRow({ row, label, text, progress, detail }) {
      if (!row) return;
      row.hidden = !detail.shipping.enabled;
      if (row.hidden) return;

      if (label) label.textContent = this.config.shipping?.label || 'Free shipping';
      if (progress) progress.style.width = `${detail.shipping.progress}%`;

      if (!text) return;
      if (detail.shipping.unlocked) {
        text.textContent = this.config.shipping?.unlockedText || 'You qualify for free shipping!';
      } else {
        const amount = this.formatMoney(detail.shipping.remainingCents, detail.currency);
        text.textContent = this.applyTemplate(this.config.shipping?.lockedText || 'Only {{ amount }} away from free shipping', {
          amount,
        });
      }
    }

    renderRewardRow({ row, text, progress, thumb, addButton, detail }) {
      if (!row) return;
      row.hidden = detail.tiers.length === 0;
      if (row.hidden) return;

      const tier = detail.nextTier || detail.activeTier;
      const rewardLabel = this.getTierRewardLabel(tier);
      const hasNext = Boolean(detail.nextTier);

      if (hasNext) {
        const remaining = Math.max(0, detail.nextTier.threshold - detail.purchaseQuantity);
        if (text) {
          text.textContent = this.applyTemplate(
            this.config.reward?.lockedText || 'Just {{ remaining }} more to get {{ reward }}',
            { remaining, reward: rewardLabel }
          );
        }
        if (progress) {
          const percent = Math.max(0, Math.min(100, (detail.purchaseQuantity / detail.nextTier.threshold) * 100));
          progress.style.width = `${percent}%`;
        }
      } else if (detail.activeTier) {
        if (text) {
          text.textContent = this.applyTemplate(
            this.config.reward?.unlockedText || 'Unlocked: {{ reward }}',
            { reward: rewardLabel }
          );
        }
        if (progress) progress.style.width = '100%';
      } else {
        if (text) text.textContent = rewardLabel;
        if (progress) progress.style.width = '0%';
      }

      this.renderTierThumb(thumb, tier);

      if (addButton) {
        const showAdd = Boolean(this.config.quickAdd?.enabled) && hasNext;
        addButton.hidden = !showAdd;
      }
    }

    renderTierThumb(element, tier) {
      if (!element) return;
      element.replaceChildren();

      const image = tier?.gifts?.find((gift) => gift?.featuredImage)?.featuredImage;
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        element.appendChild(img);
        return;
      }

      element.textContent = tier?.promotionText?.includes('%') ? '%' : '🎁';
    }

    applyTemplate(template, values = {}) {
      let output = String(template || '');
      Object.entries(values).forEach(([key, value]) => {
        output = output.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), String(value));
      });
      return output;
    }

    formatMoney(cents, currency = 'USD') {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
      }
    }

    handleDocumentClick(event) {
      const quickAddOpen = event.target.closest('[data-reward-quick-add-open]');
      if (quickAddOpen) {
        event.preventDefault();
        this.openQuickAdd(quickAddOpen);
        return;
      }

      const modalClose = event.target.closest('[data-reward-modal-close]');
      if (modalClose && this.modal?.contains(modalClose)) {
        event.preventDefault();
        this.closeQuickAdd();
        return;
      }

      const quickAddSubmit = event.target.closest('[data-reward-quick-add-submit]');
      if (quickAddSubmit && this.modal?.contains(quickAddSubmit)) {
        event.preventDefault();
        this.handleQuickAddSubmit(quickAddSubmit);
        return;
      }

      const floatingClose = event.target.closest('[data-reward-close]');
      if (floatingClose && this.floating?.contains(floatingClose)) {
        event.preventDefault();
        this.closeFloating();
        return;
      }

      if (this.floating?.open && !this.floating.contains(event.target) && !this.modal?.contains(event.target)) {
        this.floating.removeAttribute('open');
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      if (this.modal && !this.modal.hidden) {
        this.closeQuickAdd();
        return;
      }

      if (this.floating?.open) this.closeFloating();
    }

    closeFloating() {
      if (!this.floating?.open) return;
      this.floating.removeAttribute('open');
      this.floating.querySelector('summary')?.focus();
    }

    async openQuickAdd(trigger) {
      if (!this.modal || !this.config.quickAdd?.enabled) return;

      this.lastQuickAddTrigger = trigger;
      this.closeFloating();
      this.modal.hidden = false;
      document.body.classList.add('elonail-reward-modal-open');

      const grid = this.modal.querySelector('[data-reward-modal-grid]');
      const loading = this.modal.querySelector('[data-reward-modal-loading]');
      const empty = this.modal.querySelector('[data-reward-modal-empty]');
      if (grid) grid.replaceChildren();
      if (loading) loading.hidden = false;
      if (empty) empty.hidden = true;

      try {
        const products = await this.getQuickAddProducts();
        this.renderQuickAddProducts(products);
      } catch (error) {
        console.error('[Rewards] Unable to load quick add products', error);
        this.renderQuickAddProducts([]);
      } finally {
        if (loading) loading.hidden = true;
      }

      requestAnimationFrame(() => this.modal.querySelector('[data-reward-modal-close]')?.focus());
    }

    closeQuickAdd() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.body.classList.remove('elonail-reward-modal-open');
      this.lastQuickAddTrigger?.focus?.();
      this.lastQuickAddTrigger = null;
    }

    async getQuickAddProducts() {
      const quickAdd = this.config.quickAdd || {};
      const limit = Math.max(1, Number(quickAdd.limit || 8));

      if (quickAdd.source === 'collection') {
        return (quickAdd.collectionProducts || []).filter((product) => product?.available).slice(0, limit);
      }

      const cart = this.currentCart || (await this.fetchCart());
      const baseItem = cart.items.find(
        (item) => item.properties?.[GIFT_PROPERTY] !== 'true' && !item.properties?.[RULE_PROPERTY]
      );
      if (!baseItem?.product_id) return [];

      const cacheKey = `${baseItem.product_id}:${limit}`;
      if (this.recommendationCache.has(cacheKey)) return this.recommendationCache.get(cacheKey);

      const baseUrl = window.routes?.product_recommendations_url || '/recommendations/products';
      const url = `${baseUrl}.json?product_id=${encodeURIComponent(baseItem.product_id)}&limit=${limit}&intent=related`;
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const data = await response.json();
      const cartProductIds = new Set(cart.items.map((item) => Number(item.product_id)));
      const products = (data.products || [])
        .map((product) => this.normalizeProduct(product))
        .filter((product) => product.available && !cartProductIds.has(Number(product.id)))
        .slice(0, limit);

      this.recommendationCache.set(cacheKey, products);
      return products;
    }

    normalizeProduct(product) {
      const variants = Array.isArray(product.variants)
        ? product.variants.map((variant) => ({
            id: Number(variant.id),
            title: variant.title || 'Default',
            available: Boolean(variant.available),
            price: this.normalizePrice(variant.price),
          }))
        : [];

      let featuredImage = null;
      if (typeof product.featuredImage === 'string') featuredImage = product.featuredImage;
      else if (product.featuredImage?.url) featuredImage = product.featuredImage.url;
      else if (product.featuredImage?.src) featuredImage = product.featuredImage.src;
      else if (typeof product.featured_image === 'string') featuredImage = product.featured_image;
      else if (product.featured_image?.url) featuredImage = product.featured_image.url;
      else if (product.featured_image?.src) featuredImage = product.featured_image.src;
      else if (product.featured_media?.preview_image?.src) featuredImage = product.featured_media.preview_image.src;
      else if (product.featured_media?.preview_image?.url) featuredImage = product.featured_media.preview_image.url;
      else if (typeof product.images?.[0] === 'string') featuredImage = product.images[0];
      else if (product.images?.[0]?.src) featuredImage = product.images[0].src;
      else if (product.images?.[0]?.url) featuredImage = product.images[0].url;

      return {
        id: Number(product.id),
        title: product.title || '',
        handle: product.handle || '',
        url: product.url || (product.handle ? `/products/${product.handle}` : '#'),
        available: product.available !== false && variants.some((variant) => variant.available),
        featuredImage,
        price: this.normalizePrice(product.price ?? variants[0]?.price ?? 0),
        variants,
      };
    }

    normalizePrice(value) {
      if (typeof value === 'number') return Math.round(value);
      const stringValue = String(value ?? '0').trim();
      if (!stringValue) return 0;
      if (stringValue.includes('.')) return Math.round(Number.parseFloat(stringValue) * 100);
      return Number.parseInt(stringValue, 10) || 0;
    }

    renderQuickAddProducts(products) {
      if (!this.modal) return;
      const grid = this.modal.querySelector('[data-reward-modal-grid]');
      const empty = this.modal.querySelector('[data-reward-modal-empty]');
      if (!grid) return;

      grid.replaceChildren();
      const normalizedProducts = (products || []).map((product) => this.normalizeProduct(product));
      if (!normalizedProducts.length) {
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;

      normalizedProducts.forEach((product) => {
        const card = document.createElement('article');
        card.className = 'elonail-reward-product';
        card.dataset.rewardProductCard = String(product.id);

        const mediaLink = document.createElement('a');
        mediaLink.className = 'elonail-reward-product__media';
        mediaLink.href = product.url || '#';
        if (product.featuredImage) {
          const image = document.createElement('img');
          image.src = product.featuredImage;
          image.alt = product.title;
          image.loading = 'lazy';
          mediaLink.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'elonail-reward-product__placeholder';
          placeholder.textContent = product.title.slice(0, 1) || '•';
          mediaLink.appendChild(placeholder);
        }

        const body = document.createElement('div');
        body.className = 'elonail-reward-product__body';

        const title = document.createElement('a');
        title.className = 'elonail-reward-product__title';
        title.href = product.url || '#';
        title.textContent = product.title;

        const availableVariants = product.variants.filter((variant) => variant.available);
        const firstVariant = availableVariants[0];
        const price = document.createElement('span');
        price.className = 'elonail-reward-product__price';
        price.textContent = this.formatMoney(firstVariant?.price ?? product.price, this.currentCart?.currency || 'USD');

        body.append(title, price);

        if (availableVariants.length > 1) {
          const select = document.createElement('select');
          select.className = 'elonail-reward-product__variant';
          select.setAttribute('aria-label', `Choose options for ${product.title}`);
          select.dataset.rewardVariantSelect = '';
          availableVariants.forEach((variant) => {
            const option = document.createElement('option');
            option.value = String(variant.id);
            option.textContent = variant.title;
            option.dataset.price = String(variant.price);
            select.appendChild(option);
          });
          select.addEventListener('change', () => {
            const option = select.selectedOptions[0];
            if (option?.dataset.price) price.textContent = this.formatMoney(Number(option.dataset.price), this.currentCart?.currency || 'USD');
          });
          body.appendChild(select);
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--primary elonail-reward-product__button';
        button.dataset.rewardQuickAddSubmit = '';
        button.dataset.variantId = firstVariant ? String(firstVariant.id) : '';
        button.textContent = firstVariant ? 'ADD TO CART' : 'SOLD OUT';
        button.disabled = !firstVariant;
        body.appendChild(button);

        card.append(mediaLink, body);
        grid.appendChild(card);
      });
    }

    async handleQuickAddSubmit(button) {
      const card = button.closest('[data-reward-product-card]');
      if (!card || button.disabled) return;

      const select = card.querySelector('[data-reward-variant-select]');
      const variantId = Number(select?.value || button.dataset.variantId || 0);
      if (!variantId) return;

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'ADDING…';

      try {
        const url = window.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        const data = await response.json();
        if (!response.ok || data?.status) throw new Error(data.description || data.message || 'Unable to add product');

        button.textContent = 'ADDED';
        const cart = await this.fetchCart();
        this.currentCart = cart;
        await this.syncThemeCart(cart);
        this.queueReconcile(80);
        setTimeout(() => {
          if (button.isConnected) {
            button.disabled = false;
            button.textContent = originalLabel;
          }
        }, 900);
      } catch (error) {
        console.error('[Rewards] Quick add failed', error);
        button.textContent = 'TRY AGAIN';
        setTimeout(() => {
          if (button.isConnected) {
            button.disabled = false;
            button.textContent = originalLabel;
          }
        }, 1400);
      }
    }
  }

  customElements.define('elonail-rewards', ElonailRewards);
})();
